import type {LayoutPayload, PanelType} from '@github-ui/code-view-types'
import {useCurrentUser} from '@github-ui/current-user'
import safeStorage from '@github-ui/safe-storage'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import React, {createContext, useCallback, use, useEffect, useLayoutEffect, useRef, useState} from 'react'

const safeLocalStorage = safeStorage('localStorage')

export type SetPanelOpenType = (panelType: PanelType | undefined, returnFocusTarget?: HTMLElement | null) => void

// Split contexts: state is dynamic, dispatch is stable
export const OpenPanelStateContext = createContext<PanelType | undefined>(undefined)
export const OpenPanelDispatchContext = createContext<SetPanelOpenType>(() => undefined)

// Keep the combined type for backward compatibility
type OpenPanelContextType = {
  openPanel: PanelType | undefined
  setOpenPanel: SetPanelOpenType
}

/** @deprecated Use OpenPanelStateContext and OpenPanelDispatchContext directly */
export const OpenPanelContext = createContext<OpenPanelContextType>({
  openPanel: undefined,
  setOpenPanel: () => undefined,
})

/**
 * This is equivalent to a simple setState, except we will not show the
 * state as 'codeNav' or allow it to be set to 'codeNav' when on the blame page.
 * This also adds a listener to hide the panel on smaller viewports
 */
export function OpenPanelProvider({
  children,
  payload,
  openPanelRef,
  action,
}: {
  children: React.ReactNode
  payload: LayoutPayload
  openPanelRef: React.MutableRefObject<string | undefined>
  action: string
}) {
  const hasBlame = action === 'blame'
  const isTreeView = action === 'tree'

  // Use refs to keep setOpenPanel stable while still accessing current values
  const hasBlameRef = useRef(hasBlame)
  const isTreeViewRef = useRef(isTreeView)
  useLayoutEffect(() => {
    hasBlameRef.current = hasBlame
    isTreeViewRef.current = isTreeView
  })

  const currentUser = useCurrentUser()

  const returnFocusTargetRef = useRef<HTMLElement | null | undefined>(undefined)
  const [rawOpenPanel, setRawOpenPanel] = useState<PanelType | undefined>(() => {
    const storedSetting = safeLocalStorage.getItem('codeNavOpen')
    //only use the cookie if the user isn't logged in
    if (!currentUser && storedSetting !== '' && storedSetting !== null) {
      return 'codeNav'
    }
    if (currentUser && payload.symbolsExpanded) {
      return 'codeNav'
    }
  })

  const openPanel = (hasBlame || isTreeView) && rawOpenPanel === 'codeNav' ? undefined : rawOpenPanel

  React.useEffect(() => {
    openPanelRef.current = openPanel
  }, [openPanel, openPanelRef])

  const setOpenPanel = useCallback((panelType: PanelType | undefined, returnFocusTarget?: HTMLElement | null) => {
    setRawOpenPanel(currentPanel => {
      if (currentPanel && returnFocusTargetRef.current) {
        returnFocusTargetRef.current.focus()
      }
      returnFocusTargetRef.current = returnFocusTarget
      return hasBlameRef.current || (isTreeViewRef.current && panelType === 'codeNav') ? undefined : panelType
    })
  }, [])

  useHandlePanelScreenResize(setOpenPanel)

  return (
    <OpenPanelStateContext value={openPanel}>
      <OpenPanelDispatchContext value={setOpenPanel}>{children}</OpenPanelDispatchContext>
    </OpenPanelStateContext>
  )
}

// This should be SSR safe since it won't run on the server
function useHandlePanelScreenResize(setOpenPanel: (panel: PanelType | undefined) => void) {
  const {screenSize} = useScreenSize()
  const previousScreenSize = useRef(screenSize)

  useEffect(() => {
    // hide the panel if our previous render was on large screen size and the current size is smaller
    // (this prevents the panel from hiding when resizing between small/medium screen sizes)
    // or if the screenSize is a small viewport on our initial load
    const isChangingToSmallViewport = previousScreenSize.current >= ScreenSize.large
    const isInitialLoad = previousScreenSize.current === screenSize

    if (screenSize < ScreenSize.large && (isChangingToSmallViewport || isInitialLoad)) {
      setOpenPanel(undefined)
    }

    previousScreenSize.current = screenSize
  }, [screenSize, setOpenPanel])
}

export function useOpenPanel() {
  return {
    openPanel: use(OpenPanelStateContext),
    setOpenPanel: use(OpenPanelDispatchContext),
  }
}

/** Use when you only need the current panel state (will re-render when panel changes) */
export function useOpenPanelState() {
  return use(OpenPanelStateContext)
}

/** Use when you only need to set the panel (won't re-render when panel changes) */
export function useSetOpenPanel() {
  return use(OpenPanelDispatchContext)
}
